import { Avatar } from '@seek/avatar';
import { useHubble } from '@seek/hubble';
import { useTranslations } from '@vocab/react';
import { Box, Text, TooltipRenderer } from 'braid-design-system';
import { useState, useRef, useEffect } from 'react';

import { useAppConfig } from 'src/modules/appConfig';

import {
  AddPhotoDialog,
  type AddAvatarStep,
} from '../AddPhotoFlow/AddPhotoDialog';
import { AddPhotoInput } from '../AddPhotoFlow/AddPhotoInput/AddPhotoInput';
import { ImageProcessingErrorDialog } from '../AddPhotoFlow/ImageProcessingErrorDialog';
import {
  getErrorType,
  type SystemError,
  type ValidationError,
} from '../AddPhotoFlow/validateImage';
import { DeleteAvatarDialog } from '../DeleteAvatarFlow/DeleteAvatarDialog';
import { useDeleteAvatar } from '../DeleteAvatarFlow/useDeleteAvatar';

import translations from './.vocab';
import { AvatarOptionsMenuRenderer } from './AvatarOptionsMenuRenderer/AvatarOptionsMenuRenderer';
import { CameraIcon } from './CameraIcon';

import * as styles from './AvatarContainer.css';

interface AvatarContainerProps {
  avatarUrls?: {
    image128Url?: string;
  };
  loading?: boolean;
  imageError?: Error | null;
  firstName?: string;
}

export const AvatarContainer = ({
  avatarUrls,
  loading,
  imageError,
  firstName,
}: AvatarContainerProps) => {
  const { t } = useTranslations(translations);
  const hubble = useHubble();
  const { zone, language } = useAppConfig();

  // File upload state
  const [showAddPhotoDialog, setShowAddPhotoDialog] = useState(false);
  const [currentStep, setCurrentStep] =
    useState<AddAvatarStep>('UploadPreview');
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [croppedImage, setCroppedImage] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<
    ValidationError | SystemError | false
  >(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { handleDelete, isDeleting } = useDeleteAvatar({
    onClose: () => setShowDeleteDialog(false),
  });

  const { image128Url } = avatarUrls || {};
  const isAvatarImageAvailable = Boolean(image128Url);

  useEffect(() => {
    if (!showAddPhotoDialog) {
      setOriginalFile(null);
      setCroppedImage(null);
      // Reset file input value when dialog closes to ensure onChange fires on subsequent selections
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [showAddPhotoDialog]);

  const handleSetOriginalFile = (file: File | null) => {
    setOriginalFile(file);
    setCroppedImage(null);
  };

  const handleBrowseClick = () => {
    hubble.trackEvent('component_pressed', {
      currentPageSection: 'profile header',
      component: {
        type: 'button',
        name: 'upload photo',
      },
      displayLanguage: language,
      zone,
    });

    const fileInput = fileInputRef.current;

    if (fileInput) {
      // Reset file input value before opening file picker
      // This ensures the onChange event fires even if the same file is selected
      fileInput.value = '';
      // Click the input to open file picker
      fileInput.click();
    }
  };

  const handleDeleteClick = () => {
    hubble.trackEvent('component_pressed', {
      currentPageSection: 'profile header',
      component: {
        type: 'button',
        name: 'delete photo',
      },
      displayLanguage: language,
      zone,
    });
    setShowDeleteDialog(true);
  };

  const avatarComponent = (
    <Avatar
      variant="icon"
      size="xlarge"
      photoUrl={image128Url}
      photoError={Boolean(imageError)}
      icon={<CameraIcon />}
      name={firstName}
      border
      loading={loading}
      data-testid="profile-avatar"
    />
  );

  const tooltipLabel = isAvatarImageAvailable
    ? t('Update photo')
    : t('Add photo');

  return (
    <TooltipRenderer tooltip={<Text>{tooltipLabel}</Text>}>
      {({ triggerProps }) => (
        <Box {...triggerProps}>
          {isAvatarImageAvailable ? (
            <AvatarOptionsMenuRenderer
              onUploadClick={handleBrowseClick}
              onDeleteClick={handleDeleteClick}
            >
              <Box className={styles.avatarWrapperHoverable}>
                <Box
                  id="avatar-hoverable-container"
                  className={styles.avatarComponentHoverable}
                >
                  {avatarComponent}
                </Box>
              </Box>
            </AvatarOptionsMenuRenderer>
          ) : (
            <Box
              className={styles.avatarWrapperHoverable}
              aria-label="Add photo"
              role="button"
              cursor="pointer"
              onClick={loading ? undefined : handleBrowseClick}
            >
              <Box
                id="avatar-hoverable-container"
                className={styles.avatarComponentHoverable}
              >
                {avatarComponent}
              </Box>
            </Box>
          )}
          {!loading && !showAddPhotoDialog && (
            <AddPhotoInput
              navigateToCropStep={() => setCurrentStep('Crop')}
              openDialog={() => setShowAddPhotoDialog(true)}
              fileInputRef={fileInputRef}
              setOriginalFile={handleSetOriginalFile}
              setValidationError={setValidationError}
            />
          )}
          {!loading && (
            <>
              <AddPhotoDialog
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                showAddPhotoDialog={showAddPhotoDialog}
                onClose={() => setShowAddPhotoDialog(false)}
                croppedImage={croppedImage}
                setCroppedImage={setCroppedImage}
                openDialog={() => setShowAddPhotoDialog(true)}
                setValidationError={setValidationError}
                originalFile={originalFile}
                setOriginalFile={handleSetOriginalFile}
              />
              {validationError && (
                <ImageProcessingErrorDialog
                  onClose={() => setValidationError(false)}
                  errorType={getErrorType(validationError)}
                  errorMessage={validationError?.message}
                  errorData={validationError?.data}
                />
              )}
              <DeleteAvatarDialog
                open={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDelete}
                isDeleting={isDeleting}
              />
            </>
          )}
        </Box>
      )}
    </TooltipRenderer>
  );
};
